// ============================================================================
// RHSolicitacoes_Fila.js
// A aba "Todas as demandas" — UMA tela para os dois papéis:
//   • Loja (Gerente/Adm/Plantão) → vê a PRÓPRIA loja (recorte no backend) e pode
//     cancelar um envio errado.
//   • RH / Diretoria / Admin     → veem a rede e TRATAM (decidem ou concluem).
//
// ⚠️ O botão de tratativa muda com o FLUXO do tipo, que vem do catálogo:
//    DECISAO → Aprovar/Reprovar · TRATATIVA → Concluir. Não existe "aprovar
//    advertência": o fato já aconteceu, e o que o RH faz é lançar e arquivar.
//
// ⚠️ O documento abre por fetch+blob (utils/arquivoComToken), NUNCA por
//    `<a href>`: a rota é autenticada e link direto volta 401.
//
// ⚠️ NÃO EXISTE PRAZO/SLA AQUI, e é decisão, não esquecimento (15/09/2026):
//    sem uma régua acordada com o RH não há "atrasada", só contagem — e um
//    "ATRASADA" vermelho inventado por nós vira ruído que a rede aprende a
//    ignorar. O que existe é a coluna TEMPO (quanto esperou) e a ordenação por
//    ela. No dia em que o RH definir o prazo de cada tipo, ele nasce no catálogo
//    (solicitacoes-tipos.js) e esta tela só compara.
//
// ⚠️ O MODAL É UM SÓ, e serve a dois momentos: PENDENTE abre em modo AÇÃO
//    (tratar ou cancelar) e encerrada abre em modo LEITURA (o botão vira
//    "Detalhes"). É ali que a OBSERVAÇÃO DO RH aparece — para a loja também, e
//    esse é o ponto: ela costuma dizer o que a loja precisa fazer em seguida, e
//    até 16/09/2026 não era exibida para ninguém, nem para quem a escreveu.
//    Duas telas para o mesmo registro divergiriam no primeiro ajuste feito de
//    um lado só.
//
// ⚠️ SITUAÇÃO na tela são TRÊS (Pendente/Aprovada/Reprovada) e no banco são
//    CINCO. O agrupamento é o MESMO dos cards e do filtro (22/09): Aprovada =
//    APROVADO + CONCLUIDO; Reprovada = REPROVADO + CANCELADO. O filtro é
//    traduzido no backend (GRUPOS_STATUS); a coluna traduz aqui (GRUPO). O único
//    subtexto que sobrou é "cancelada" sob um CANCELADO — ver DETALHE_STATUS.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, Ban, CheckCircle2, ChevronsUpDown, FileText, Inbox, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { estilos, BadgeStatus } from '../../processoseletivo/psUI';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import Paginacao, { POR_PAGINA } from '../../Paginacao';

const COR = '#e11d48';

// Cards de panorama (contadores por situação) — estilos locais, para não mexer no
// `estilos` compartilhado do psUI por causa de um card só do RH.
const cardsPanorama = { display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18 };
const cardPanorama = {
  flex: '1 1 140px', minWidth: 120, padding: '14px 16px',
  border: '1px solid #e2e8f0', borderRadius: 12,
};

const fmtData = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
};
// 'AAAA-MM-DD HH:MM' → 'DD/MM/AAAA às HH:MM'. O backend já devolve no fuso do
// servidor (Brasília) — aqui é só recorte de string, sem `new Date`, que
// reinterpretaria o texto como UTC em alguns navegadores.
const fmtDataHora = (v) => {
  if (!v) return '—';
  const [dia, hora] = String(v).split(' ');
  return hora ? `${fmtData(dia)} às ${hora}` : fmtData(dia);
};

// O que o RH fez, na palavra do fluxo: aprovar não é o mesmo que concluir.
const ROTULO_DESFECHO = { APROVADO: 'Aprovada', REPROVADO: 'Reprovada', CONCLUIDO: 'Concluída' };

// 'AAAA-MM' → 'MM/AAAA'
const fmtCompetencia = (v) => (v ? v.split('-').reverse().join('/') : '—');

// ---------------------------------------------------------------------
// TEMPO DE FILA — a coluna que responde "está parada há quanto tempo?".
//
// ⚠️ A conta é em DIAS DE CALENDÁRIO, não em horas: o RH cobra "está parada há
// três dias", e 2h50 de diferença virando "0 dia" confundiria mais do que
// ajudaria. Por isso as duas pontas são zeradas na meia-noite LOCAL.
//
// ⚠️ Meia-noite local, e não `toISOString()`: o ISO é UTC e, depois das 21h em
// Brasília, ele já está no dia seguinte — todo cálculo da noite sairia 1 dia
// maior.
// ---------------------------------------------------------------------
const MS_POR_DIA = 24 * 60 * 60 * 1000;

const meiaNoiteLocal = (v) => {
  if (!v) return null;
  const [a, m, d] = String(v).slice(0, 10).split('-').map(Number);
  return a && m && d ? new Date(a, m - 1, d) : null;
};

const diasCorridos = (de, ate) => {
  const inicio = meiaNoiteLocal(de);
  if (!inicio) return null;
  const fim = ate ? meiaNoiteLocal(ate) : new Date(new Date().setHours(0, 0, 0, 0));
  if (!fim) return null;
  return Math.max(0, Math.round((fim - inicio) / MS_POR_DIA));
};

const plural = (n) => (n === 1 ? '1 dia' : `${n} dias`);

// Cor do tempo de espera de uma PENDENTE. Os cortes são de cobrança, não
// estéticos: até uma semana é o giro normal; passou de 15 dias, alguém esqueceu.
const tomDaEspera = (dias) => {
  if (dias >= 15) return '#b91c1c';
  if (dias >= 8) return '#b45309';
  return '#475569';
};

// ---------------------------------------------------------------------
// O texto da coluna Tempo, conforme a situação da linha.
//   PENDENTE  → há quanto tempo espera (é o número que o RH persegue).
//   encerrada → quanto tempo LEVOU, da entrada à tratativa/cancelamento.
// ⚠️ Linha antiga sem data de encerramento gravada cai no travessão em vez de
// inventar "0 dias" — não saber é diferente de ter sido no mesmo dia.
// ---------------------------------------------------------------------
const tempoDaLinha = (s) => {
  if (s.status === 'PENDENTE') {
    const dias = diasCorridos(s.dataSolicitacao);
    if (dias === null) return { texto: '—', detalhe: '', cor: '#94a3b8' };
    return {
      texto: dias === 0 ? 'Hoje' : `há ${plural(dias)}`,
      detalhe: 'aguardando o RH',
      cor: tomDaEspera(dias),
      forte: true,
    };
  }
  const fim = s.status === 'CANCELADO' ? s.canceladoEm : s.dataTratativa;
  const dias = diasCorridos(s.dataSolicitacao, fim);
  if (dias === null) return { texto: '—', detalhe: '', cor: '#94a3b8' };
  return {
    texto: dias === 0 ? 'no mesmo dia' : `em ${plural(dias)}`,
    detalhe: fmtData(fim),
    cor: '#64748b',
  };
};

// ---------------------------------------------------------------------
// SITUAÇÃO — três grupos sobre os cinco status do banco (espelha GRUPOS_STATUS
// do server-solicitacoes.js). ⚠️ O espelho aqui é de RÓTULO, não de regra: a
// peneira do filtro é feita no servidor; esta função só decide o que desenhar.
// ---------------------------------------------------------------------
// A coluna Situação agora usa os MESMOS três grupos dos cards e do filtro
// (pedido da Julia, 22/09): Aprovada = APROVADO + CONCLUIDO; Reprovada =
// REPROVADO + CANCELADO. ⚠️ O ENUM do banco NÃO muda — isto é só rótulo de tela,
// e por isso dá para voltar atrás no agrupamento sem migration.
const GRUPO = { PENDENTE: 'PENDENTE', APROVADO: 'APROVADA', CONCLUIDO: 'APROVADA', REPROVADO: 'REPROVADA', CANCELADO: 'REPROVADA' };
const grupoDe = (status) => GRUPO[status] || status;
// Ordem de leitura da coluna Situação: o que espera vem antes do que acabou.
const PESO_GRUPO = { PENDENTE: 0, APROVADA: 1, REPROVADA: 2 };

// Subtexto só quando ele acrescenta ao rótulo do grupo. CANCELADO cai em
// "Reprovada" (é assim que os cards e o filtro contam), mas um cancelamento
// costuma ser a própria loja corrigindo um envio errado — não uma reprovação do
// RH. O "cancelada" embaixo preserva essa diferença sem inventar um quarto selo.
const DETALHE_STATUS = { CANCELADO: 'cancelada' };

const selo = (status) => {
  const g = grupoDe(status);
  const detalhe = DETALHE_STATUS[status];
  const badge = g === 'PENDENTE'
    ? <BadgeStatus tom="emProcesso">Pendente</BadgeStatus>
    : g === 'APROVADA'
      ? <BadgeStatus tom="aprovado">Aprovada</BadgeStatus>
      : g === 'REPROVADA'
        ? <BadgeStatus tom="reprovado">Reprovada</BadgeStatus>
        : <BadgeStatus tom="neutro">{status}</BadgeStatus>;
  if (!detalhe) return badge;
  return (
    <>
      {badge}
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{detalhe}</div>
    </>
  );
};

// ---------------------------------------------------------------------
// MÊS DE ENVIO — o filtro por data de envio (MM/AAAA) vira as duas pontas que
// o backend entende. ⚠️ O último dia sai de `new Date(ano, mes, 0)`: dia 0 do
// mês seguinte é o último do mês pedido, e isso resolve fevereiro e ano
// bissexto sem tabela de dias.
// ---------------------------------------------------------------------
// 'AAAA-MM' do mes corrente. Nao e `toISOString().slice(0,7)`: aquele e UTC e,
// na virada do mes depois das 21h em Brasilia, abriria a tela no mes seguinte.
const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const mesParaIntervalo = (mes) => {
  if (!/^\d{4}-\d{2}$/.test(String(mes || ''))) return null;
  const [a, m] = mes.split('-').map(Number);
  const ultimo = new Date(a, m, 0).getDate();
  return { de: `${mes}-01`, ate: `${mes}-${String(ultimo).padStart(2, '0')}` };
};

function RHSolicitacoesFila({ tipoInicial = '', tipos = [], podeTratar }) {
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState(tipoInicial);
  // ⚠️ Abre em TODAS, não em pendentes: esta é a única porta para a fila desde
  // que o "Consultar" saiu do card, e o RH precisa enxergar também o que já foi
  // concluído ou cancelado. A ordenação do backend já joga as pendentes no topo.
  const [filtroStatus, setFiltroStatus] = useState('');
  // Panorama por status (cards do topo). Vem do backend porque a fila é peneirada
  // pelo teto de 500 e pelo próprio filtro de status — contar na tela mentiria.
  const [contadores, setContadores] = useState({ PENDENTE: 0, APROVADO: 0, REPROVADO: 0, CONCLUIDO: 0, CANCELADO: 0 });
  // Mês do ENVIO (AAAA-MM). ⚠️ É a data do envio, não a do fato: é a única data
  // que TODA solicitação tem preenchida, e é por ela que o RH procura ("o que
  // entrou em agosto?"). Vai como intervalo para o backend (de/ate).
  // ⚠️ Abre no MÊS CORRENTE, não em "todos": é o recorte que o RH usa no dia a
  // dia, e a fila inteira sem corte é histórico, não trabalho. Consequência
  // assumida: uma pendente que entrou no mês passado NÃO aparece na abertura —
  // para achá-la, troque o mês ou limpe o campo.
  const [filtroMes, setFiltroMes] = useState(mesAtual());
  // ORDENAÇÃO PELO CABEÇALHO. `campo: null` = ordem natural do backend
  // (pendentes no topo, envio mais recente primeiro) — é o estado em que a tela
  // abre, porque é o que responde "o que eu tenho para fazer agora?".
  const [ordenacao, setOrdenacao] = useState({ campo: null, dir: 'desc' });
  const [pagina, setPagina] = useState(1);
  const [alvo, setAlvo] = useState(null);          // solicitação aberta no modal
  const [observacao, setObservacao] = useState('');
  // Anexo OPCIONAL que o RH junta ao concluir/decidir (comprovante de cadastro
  // do VT, aditivo assinado, espelho corrigido). Opcional de verdade: a maioria
  // das tratativas não gera papel de volta.
  const [arquivoRh, setArquivoRh] = useState(null);
  // "Passa a valer em" — só existe nos tipos que declaram `vigencia` no
  // catálogo (VT e Falta de Marcação). Nesses é OBRIGATÓRIA para concluir ou
  // aprovar: é a data que diz à loja a partir de quando o ato tem efeito.
  const [dataVigencia, setDataVigencia] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(null);

  // Rótulo e fluxo vêm do catálogo carregado pelo sub-hub — a tela não tem lista própria.
  const defDe = useCallback((chave) => tipos.find((t) => t.chave === chave) || null, [tipos]);

  // A vigência do tipo aberto no modal (ou null). Vem do catálogo pela API — a
  // tela não tem lista própria de quais tipos têm "passa a valer em".
  const vigenciaDoAlvo = alvo ? (defDe(alvo.tipo) || {}).vigencia || null : null;
  const exigeVigencia = !!vigenciaDoAlvo;
  // Observação do RH obrigatória — 2ª Via do Cartão VT e Pedido de Demissão
  // (o Término do Contrato de Experiência também, mas ele é tratado no módulo
  // próprio). Quem declara é o catálogo, como tudo aqui.
  const exigeObsRh = !!(alvo && (defDe(alvo.tipo) || {}).observacaoRhObrigatoria);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const qs = new URLSearchParams();
      if (filtroTipo) qs.set('tipo', filtroTipo);
      if (filtroStatus) qs.set('status', filtroStatus);
      const intervalo = mesParaIntervalo(filtroMes);
      if (intervalo) { qs.set('de', intervalo.de); qs.set('ate', intervalo.ate); }
      const resp = await fetch(`/api/rh/solicitacoes?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Não foi possível carregar as solicitações.');
      setLinhas(await resp.json());

      // Contadores dos cards — mesmos recortes MENOS o status (o panorama não
      // pode depender da situação selecionada). Não derruba a fila se falhar.
      const qsc = new URLSearchParams();
      if (filtroTipo) qsc.set('tipo', filtroTipo);
      if (intervalo) { qsc.set('de', intervalo.de); qsc.set('ate', intervalo.ate); }
      try {
        const rc = await fetch(`/api/rh/solicitacoes/contadores?${qsc.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (rc.ok) setContadores(await rc.json());
      } catch { /* card é acessório — silêncio se falhar */ }
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [filtroTipo, filtroStatus, filtroMes]);

  useEffect(() => { carregar(); }, [carregar]);

  // Trocar filtro ou ordem sem voltar para a página 1 deixa o usuário olhando
  // uma página que não existe mais na nova seleção (tela vazia sem motivo).
  useEffect(() => { setPagina(1); }, [filtroTipo, filtroStatus, filtroMes, ordenacao]);

  // -------------------------------------------------------------------
  // ORDENAÇÃO PELO CABEÇALHO — o valor que cada coluna compara.
  //
  // ⚠️ Texto e número comparam diferente, e por isso cada chave devolve UM tipo
  // só: `dias` é number (2 antes de 10), o resto é string comparada por
  // localeCompare (acento no lugar certo). Data em ISO é string ordenável — é
  // de propósito que o backend devolve 'AAAA-MM-DD HH:MM'.
  //
  // ⚠️ `documento` cai na competência quando não há data do fato: é o histórico
  // da Falta de Marcação, que era mensal até 16/09/2026.
  // -------------------------------------------------------------------
  const valorDaColuna = useCallback((s2, campo) => {
    switch (campo) {
      case 'solicitacao': { const d = defDe(s2.tipo); return d ? d.rotulo : s2.tipo || ''; }
      case 'funcionario': return s2.funcionario || '';
      case 'loja': return s2.loja || '';
      case 'enviado': return s2.dataSolicitacao || '';
      case 'situacao': return PESO_GRUPO[grupoDe(s2.status)] ?? 9;
      // Tempo = dias de espera (pendente) ou dias até a baixa (encerrada).
      case 'tempo': {
        const fim = s2.status === 'PENDENTE' ? null : (s2.status === 'CANCELADO' ? s2.canceladoEm : s2.dataTratativa);
        const dias = s2.status === 'PENDENTE' ? diasCorridos(s2.dataSolicitacao) : diasCorridos(s2.dataSolicitacao, fim);
        return dias === null ? -1 : dias;
      }
      default: return '';
    }
  }, [defDe]);

  // -------------------------------------------------------------------
  // O que a tabela mostra: a fila do backend, reordenada pelo cabeçalho.
  //
  // ⚠️ Sem ordenação escolhida, a ordem é a do backend E as pendentes ficam no
  // topo — é o mesmo critério do `ORDER BY` do SQL. Assim que o usuário clica
  // num cabeçalho, manda o clique: separar pendentes ali faria a seta mentir
  // ("ordenei por loja e não veio em ordem de loja").
  // -------------------------------------------------------------------
  const ordenadas = useMemo(() => {
    if (!ordenacao.campo) return linhas;
    const sinal = ordenacao.dir === 'asc' ? 1 : -1;
    return [...linhas].sort((a, b) => {
      const va = valorDaColuna(a, ordenacao.campo);
      const vb = valorDaColuna(b, ordenacao.campo);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sinal;
      return String(va).localeCompare(String(vb), 'pt-BR') * sinal;
    });
  }, [linhas, ordenacao, valorDaColuna]);

  // A fatia da página atual. ⚠️ `paginaSegura` evita a tela em branco quando a
  // lista encolhe (filtro novo) e a página guardada já não existe.
  const paginaSegura = Math.min(pagina, Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA)));
  const visiveis = useMemo(
    () => ordenadas.slice((paginaSegura - 1) * POR_PAGINA, paginaSegura * POR_PAGINA),
    [ordenadas, paginaSegura],
  );

  // Clique no cabeçalho: 1º clique ordena, clique de novo inverte.
  const alternarOrdem = (campo) => setOrdenacao((o) => (
    o.campo === campo ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: 'asc' }
  ));

  const abrirDocumento = async (s) => {
    setErro(null);
    try {
      await abrirArquivoComToken(`/api/rh/solicitacoes/${s.id}/arquivo`, s.arquivoNome || `solicitacao_${s.id}`);
    } catch (e) {
      setErro(e.message);
    }
  };

  // Anexos ADICIONAIS (2º documento em diante) — só existem nos tipos que
  // aceitam mais de um (Falta de Marcação). Rota autenticada própria, com o
  // mesmo recorte por loja do documento principal.
  const abrirAnexoExtra = async (s, anexo) => {
    setErro(null);
    try {
      await abrirArquivoComToken(`/api/rh/solicitacoes/${s.id}/anexos/${anexo.id}`, anexo.nome || `anexo_${anexo.id}`);
    } catch (e) {
      setErro(e.message);
    }
  };

  // A resposta do RH: mesma rota autenticada do documento da loja, com o mesmo
  // recorte por loja no servidor.
  const abrirRespostaRh = async (s) => {
    setErro(null);
    try {
      await abrirArquivoComToken(`/api/rh/solicitacoes/${s.id}/arquivo-rh`, s.arquivoRhNome || `resposta_rh_${s.id}`);
    } catch (e) {
      setErro(e.message);
    }
  };

  const tratar = async (status) => {
    if (!alvo || salvando) return;
    // A mesma trava existe no servidor; aqui ela existe para o RH ver o aviso
    // no campo, e não como erro genérico depois do envio.
    if (exigeObsRh && !observacao.trim()) {
      return setFeedbackModal({ tipo: 'erro', msg: 'Escreva a observação do RH — ela é obrigatória nesta demanda.' });
    }
    if (exigeVigencia && status !== 'REPROVADO' && !dataVigencia) {
      return setFeedbackModal({ tipo: 'erro', msg: `Informe o campo "${vigenciaDoAlvo.rotulo}".` });
    }
    setSalvando(true);
    setFeedbackModal(null);
    try {
      const token = localStorage.getItem('authToken');
      // ⚠️ FormData, e NÃO JSON: a tratativa pode levar anexo. O
      // 'Content-Type' fica de fora de propósito — quem escreve o header (com
      // o boundary) é o navegador, e declará-lo à mão quebra o multipart.
      const corpo = new FormData();
      corpo.append('status', status);
      if (observacao.trim()) corpo.append('observacao', observacao.trim());
      // ⚠️ Não vai em REPROVADO: reprovar não faz nada passar a valer (o
      // servidor descarta a data nesse caso — aqui é só não mandar).
      if (exigeVigencia && status !== 'REPROVADO') corpo.append('dataVigencia', dataVigencia);
      if (arquivoRh) corpo.append('arquivo', arquivoRh);
      const resp = await fetch(`/api/rh/solicitacoes/${alvo.id}/tratativa`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: corpo,
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados.message || 'Erro ao registrar a tratativa.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  const cancelar = async () => {
    if (!alvo || salvando) return;
    if (!motivo.trim()) return setFeedbackModal({ tipo: 'erro', msg: 'Informe o motivo do cancelamento.' });
    setSalvando(true);
    setFeedbackModal(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/rh/solicitacoes/${alvo.id}/cancelar`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados.message || 'Erro ao cancelar.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  // Reabrir uma demanda TRATADA: volta para PENDENTE para tratar de novo. Só
  // quem trata (o botão só aparece para podeTratar). A decisão atual é guardada
  // no histórico pelo servidor — reusa o campo `motivo`, que no modo leitura de
  // uma tratada não é disputado por nenhuma outra ação (cancelar só vale para
  // pendente).
  const reabrir = async () => {
    if (!alvo || salvando) return;
    if (!motivo.trim()) return setFeedbackModal({ tipo: 'erro', msg: 'Informe o motivo da reabertura.' });
    setSalvando(true);
    setFeedbackModal(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/rh/solicitacoes/${alvo.id}/reabrir`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados.message || 'Erro ao reabrir.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  const abrirModal = (s) => {
    setAlvo(s); setObservacao(''); setArquivoRh(null); setMotivo(''); setDataVigencia(''); setFeedbackModal(null);
  };

  return (
    <div>
      <h2 style={estilos.tituloSecao}>Demandas para RH</h2>
      <p style={estilos.subtituloSecao}>
        {podeTratar
          ? 'Documentos enviados pelas lojas. A coluna Tempo mostra há quantos dias a demanda espera — abra o anexo antes de registrar a tratativa.'
          : 'Os documentos que a sua loja enviou ao RH.'}
      </p>

      {/* CARDS DE PANORAMA — quantidade por situação. São INDICADORES (não filtram):
          o pedido foi ver de relance quantas demandas estão pendentes, aprovadas e
          reprovadas. Respeitam os filtros de demanda/mês.
          ⚠️ AGRUPAMENTO (regra da Julia, 21/09): "Aprovadas" engloba APROVADO **e**
          CONCLUIDO (o fluxo de tratativa concluído conta como aprovado); "Reprovadas"
          engloba REPROVADO **e** CANCELADO. Não confundir com o GRUPO da coluna
          Situação, que junta tudo em Concluída/Cancelada — aqui a régua é outra.
          Cores semânticas iguais às dos botões de tratativa (verde/vermelho). */}
      <div style={cardsPanorama}>
        {[
          { rotulo: 'Pendentes', valor: contadores.PENDENTE, cor: '#b45309', fundo: '#fffbeb', borda: '#fde68a' },
          { rotulo: 'Aprovadas', valor: contadores.APROVADO + contadores.CONCLUIDO, cor: '#047857', fundo: '#f0fdf4', borda: '#bbf7d0' },
          { rotulo: 'Reprovadas', valor: contadores.REPROVADO + contadores.CANCELADO, cor: '#b91c1c', fundo: '#fef2f2', borda: '#fecaca' },
        ].map((c) => (
          <div key={c.rotulo} style={{ ...cardPanorama, background: c.fundo, borderColor: c.borda }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c.cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {c.valor}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginTop: 6 }}>{c.rotulo}</div>
          </div>
        ))}
      </div>

      <div style={{ ...estilos.barraFiltros, marginTop: 20 }}>
        <div>
          <label style={estilos.rotuloFiltro}>Demanda</label>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} style={estilos.input}>
            <option value="">Todas</option>
            {tipos.filter((t) => t.formato === 'documento').map((t) => (
              <option key={t.chave} value={t.chave}>{t.rotulo}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={estilos.rotuloFiltro}>Situação</label>
          {/* ⚠️ O filtro espelha os CARDS de panorama (pedido da Julia, 22/09):
              Aprovadas = APROVADO + CONCLUIDO; Reprovadas = REPROVADO + CANCELADO.
              Quem traduz o grupo para os status crus é o backend (GRUPOS_STATUS).
              A coluna Situação segue com o seu próprio agrupamento (Concluída/
              Cancelada) + o subtexto aprovado/reprovado — são réguas diferentes. */}
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} style={estilos.input}>
            <option value="">Todas</option>
            <option value="PENDENTE">Pendentes</option>
            <option value="APROVADAS">Aprovadas</option>
            <option value="REPROVADAS">Reprovadas</option>
          </select>
        </div>
        <div>
          {/* Mês do ENVIO. O `type=month` já mostra MM/AAAA em PT-BR e entrega
              AAAA-MM — sem máscara escrita à mão, que é onde esse campo costuma
              quebrar no celular. */}
          <label style={estilos.rotuloFiltro}>Data de envio</label>
          <input type="month" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)} style={estilos.input} />
        </div>
      </div>

      {erro && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 14 }}>
          {erro}
        </div>
      )}

      {carregando ? (
        <p style={{ color: '#64748b', fontSize: 14 }}>Carregando…</p>
      ) : visiveis.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <Inbox size={36} />
          <p style={{ marginTop: 12, fontSize: 14 }}>
            {filtroMes
              ? `Nenhuma demanda enviada em ${fmtCompetencia(filtroMes)} nesta seleção.`
              : 'Nenhuma demanda nesta seleção.'}
          </p>
        </div>
      ) : (
        <div style={estilos.tabelaWrap}>
          <div style={{ overflowX: 'auto' }}>
            <table style={estilos.tabela}>
              <thead>
                <tr>
                  <ThOrdenavel campo="solicitacao" ordenacao={ordenacao} aoClicar={alternarOrdem}>Solicitação</ThOrdenavel>
                  <ThOrdenavel campo="funcionario" ordenacao={ordenacao} aoClicar={alternarOrdem}>Funcionário</ThOrdenavel>
                  <ThOrdenavel campo="loja" ordenacao={ordenacao} aoClicar={alternarOrdem}>Loja</ThOrdenavel>
                  <ThOrdenavel campo="enviado" ordenacao={ordenacao} aoClicar={alternarOrdem}>Enviado</ThOrdenavel>
                  <ThOrdenavel campo="situacao" ordenacao={ordenacao} aoClicar={alternarOrdem}>Situação</ThOrdenavel>
                  <ThOrdenavel campo="tempo" ordenacao={ordenacao} aoClicar={alternarOrdem}>Tempo</ThOrdenavel>
                  <th style={estilos.th}>Documento</th>
                  <th style={estilos.th}></th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((s) => {
                  const def = defDe(s.tipo);
                  return (
                    <tr key={s.id}>
                      <td style={estilos.td}>{def ? def.rotulo : s.tipo}</td>
                      <td style={estilos.td}>
                        <div style={{ fontWeight: 700 }}>{s.funcionario}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{s.cargo || '—'}</div>
                      </td>
                      <td style={estilos.td}>{s.loja || '—'}</td>
                      {/* ⚠️ NÃO EXISTE COLUNA "data do documento", e é decisão: desde
                          15/09/2026 a DATA_REFERENCIA dos tipos de evento é a data do
                          ENVIO (o campo do fato saiu do formulário), então a coluna
                          repetiria esta aqui. A COMPETÊNCIA aparece como subtexto
                          porque ela ainda diz algo que o envio não diz — é o
                          histórico da Falta de Marcação, mensal até 16/09/2026. */}
                      <td style={{ ...estilos.td, whiteSpace: 'nowrap' }}>
                        <div>{s.dataSolicitacao || '—'}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{s.solicitante || '—'}</div>
                        {s.competencia && (
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>
                            competência {fmtCompetencia(s.competencia)}
                          </div>
                        )}
                      </td>
                      {/* A vigência entra como subtexto da Situação, e não como
                          coluna nova: só quatro dos oito tipos a têm, e uma
                          coluna vazia na maioria das linhas custa largura sem
                          informar. Aqui ela fica colada ao "Concluída", que é
                          exatamente o que ela qualifica. */}
                      <td style={estilos.td}>
                        {selo(s.status)}
                        {s.dataVigencia && (
                          <div style={{ fontSize: 12, color: '#047857', fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap' }}>
                            vale a partir de {fmtData(s.dataVigencia)}
                          </div>
                        )}
                        {/* Marca uma demanda que já foi devolvida para pendente ao
                            menos uma vez — inclusive quando ela voltou a ser
                            PENDENTE agora, para que se saiba que já houve decisão. */}
                        {s.reaberturas > 0 && (
                          <div style={{ fontSize: 12, color: '#b45309', fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <RotateCcw size={12} /> reaberta {s.reaberturas}×
                          </div>
                        )}
                      </td>
                      {/* Tempo: dias de espera das pendentes, dias até a baixa das encerradas. */}
                      <td style={{ ...estilos.td, whiteSpace: 'nowrap' }}>
                        {(() => {
                          const t = tempoDaLinha(s);
                          return (
                            <>
                              <div style={{ color: t.cor, fontWeight: t.forte ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
                                {t.texto}
                              </div>
                              {t.detalhe && <div style={{ fontSize: 12, color: '#94a3b8' }}>{t.detalhe}</div>}
                            </>
                          );
                        })()}
                      </td>
                      <td style={estilos.td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                          {/* Com anexos extras (Falta de Marcação), o principal
                              vira "Documento 1" e cada extra ganha o próximo número
                              — sem eles, segue o "Abrir" de sempre. */}
                          <button onClick={() => abrirDocumento(s)} style={estiloBotaoAnexo(COR)}>
                            <FileText size={14} /> {s.anexosExtras?.length ? 'Documento 1' : 'Abrir'}
                          </button>
                          {(s.anexosExtras || []).map((a, i) => (
                            <button key={a.id} onClick={() => abrirAnexoExtra(s, a)} style={estiloBotaoAnexo(COR)}>
                              <FileText size={14} /> Documento {i + 2}
                            </button>
                          ))}
                          {/* ⚠️ O segundo botão só existe quando o RH anexou algo.
                              Sem ele, a resposta do RH ficaria escondida atrás de
                              um clique em "Detalhes" e a loja não saberia que ela
                              existe — que é o problema que este anexo veio
                              resolver, não criar de novo. */}
                          {s.arquivoRhNome && (
                            <button onClick={() => abrirRespostaRh(s)} style={estiloBotaoAnexo('#047857')}>
                              <FileText size={14} /> Resposta do RH
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={estilos.td}>
                        {/* Pendente abre o modal em modo AÇÃO; encerrada, em modo
                            LEITURA — é lá que a observação do RH aparece, para a
                            loja também. */}
                        <button
                          onClick={() => abrirModal(s)}
                          style={s.status === 'PENDENTE'
                            ? { padding: '6px 14px', borderRadius: 6, border: 'none',
                                background: COR, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }
                            : { padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0',
                                background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {s.status === 'PENDENTE' ? (podeTratar ? 'Tratar' : 'Cancelar') : 'Detalhes'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!carregando && ordenadas.length > 0 && (
        <Paginacao
          total={ordenadas.length}
          pagina={paginaSegura}
          onMudar={setPagina}
          cor={COR}
          fundo="#fff1f2"
        />
      )}

      {/* ------------------------------------------------------------------
          Modal: tratativa (RH) ou cancelamento (loja). Os botões saem do FLUXO
          do tipo — quem só pode cancelar não vê Aprovar/Concluir.
      ------------------------------------------------------------------ */}
      {alvo && (
        <div
          onClick={() => !salvando && setAlvo(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 28, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              {defDe(alvo.tipo) ? defDe(alvo.tipo).rotulo : alvo.tipo}
            </h3>
            <p style={{ margin: '6px 0 20px', fontSize: 14, color: '#64748b' }}>
              {alvo.funcionario} · {alvo.loja} ·{' '}
              {alvo.competencia ? fmtCompetencia(alvo.competencia) : fmtData(alvo.dataSolicitacao)}
            </p>

            {alvo.reaberturas > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#b45309', fontWeight: 700, margin: '0 0 16px' }}>
                <RotateCcw size={15} /> Esta demanda já foi reaberta {alvo.reaberturas}×.
              </div>
            )}

            {alvo.observacao && (
              <p style={{ fontSize: 13, color: '#475569', background: '#f8fafc', padding: 12, borderRadius: 8, margin: '0 0 18px' }}>
                <strong>Observação da loja:</strong> {alvo.observacao}
              </p>
            )}

            {/* ------------------------------------------------------------
                MODO LEITURA — linha já encerrada. É AQUI que a observação do RH
                aparece, e ela aparece para TODOS os perfis que veem a fila: a
                loja precisa saber o que o RH respondeu (e costuma haver ali a
                instrução do que fazer em seguida).
            ------------------------------------------------------------ */}
            {alvo.status !== 'PENDENTE' && (
              <div style={{
                border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 18,
                background: alvo.status === 'CANCELADO' ? '#f8fafc' : '#f0fdf4',
              }}>
                {alvo.status === 'CANCELADO' ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#475569', marginBottom: 6 }}>
                      Cancelada por {alvo.canceladoPor || '—'} · {fmtDataHora(alvo.canceladoEm)}
                    </div>
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      <strong>Motivo:</strong> {alvo.canceladoMotivo || '—'}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#047857', marginBottom: 6 }}>
                      {ROTULO_DESFECHO[alvo.status] || 'Tratada'} por {alvo.tratadoPor || '—'}
                      {' · '}{fmtDataHora(alvo.dataTratativa)}
                    </div>
                    {/* A data só aparece quando existe: no histórico anterior a
                        16/09/2026 ela é NULL, e um "—" ali sugeriria que o RH
                        deixou de preencher algo que nem era pedido. */}
                    {alvo.dataVigencia && (
                      <div style={{ fontSize: 13, color: '#047857', fontWeight: 700, marginBottom: 6 }}>
                        {(defDe(alvo.tipo) || {}).vigencia
                          ? (defDe(alvo.tipo).vigencia.rotulo)
                          : 'Passa a valer em'}: {fmtData(alvo.dataVigencia)}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>
                      <strong>Observação do RH:</strong>{' '}
                      {alvo.observacaoRh || <span style={{ color: '#94a3b8' }}>sem observação</span>}
                    </div>
                    {alvo.arquivoRhNome && (
                      <button
                        onClick={() => abrirRespostaRh(alvo)}
                        style={{ ...estiloBotaoAnexo('#047857'), marginTop: 12 }}
                      >
                        <FileText size={14} /> Abrir resposta do RH
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* REABRIR — em qualquer demanda ENCERRADA (tratada OU cancelada) e
                só para quem trata. Devolve para PENDENTE; o desfecho atual (a
                decisão ou o cancelamento acima) vai para o histórico no servidor
                (não se perde). Cor âmbar: é um "desfazer", não um ato destrutivo
                nem uma tratativa. */}
            {alvo.status !== 'PENDENTE' && podeTratar && (
              <div style={{ border: '1px solid #fed7aa', background: '#fff7ed', borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#b45309', marginBottom: 6 }}>Reabrir demanda</div>
                <p style={{ fontSize: 13, color: '#7c2d12', margin: '0 0 10px' }}>
                  Devolve a demanda para <strong>Pendente</strong> para tratar de novo. O desfecho acima fica guardado no histórico — não é apagado.
                </p>
                <label style={estilos.rotuloFiltro}>
                  Motivo da reabertura <span style={{ color: '#b45309' }}>*</span>
                </label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={2}
                  placeholder="Ex.: tratativa lançada no tipo errado; comprovante anexado estava incorreto."
                  style={{ ...estilos.input, resize: 'vertical' }}
                />
                <button onClick={reabrir} disabled={salvando} style={{ ...botao('#b45309'), marginTop: 12 }}>
                  <RotateCcw size={16} /> Reabrir demanda
                </button>
              </div>
            )}

            {/* MODO AÇÃO — só enquanto a solicitação está pendente. */}
            {alvo.status === 'PENDENTE' && (podeTratar ? (
              <>
                {/* ⚠️ VEM PRIMEIRO, e é o único campo obrigatório do modal: nos
                    tipos de VT e na Falta de Marcação, a demanda não se encerra
                    quando o RH dá baixa — ela COMEÇA. Se este campo ficasse
                    depois do anexo, seria o último visto e o primeiro esquecido. */}
                {exigeVigencia && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={{ ...estilos.rotuloFiltro, display: 'block' }}>
                      {vigenciaDoAlvo.rotulo} <span style={{ color: COR }}>*</span>
                    </label>
                    <input
                      type="date"
                      value={dataVigencia}
                      onChange={(e) => setDataVigencia(e.target.value)}
                      style={{ ...estilos.input, maxWidth: 220 }}
                    />
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                      {vigenciaDoAlvo.ajuda} A loja vê esta data na fila.
                    </div>
                  </div>
                )}

                {/* ⚠️ O rótulo MUDA com o tipo, e isso é o aviso: "(opcional)"
                    num campo que o botão vai exigir é a receita do clique que
                    volta com erro. */}
                <label style={estilos.rotuloFiltro}>
                  {exigeObsRh ? <>Observação do RH <span style={{ color: COR }}>*</span></> : 'Observação do RH (opcional)'}
                </label>
                <textarea
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  rows={3}
                  placeholder={exigeObsRh ? 'A loja lê este texto — diga o que ela precisa saber para seguir.' : ''}
                  style={{ ...estilos.input, resize: 'vertical' }}
                />
                {/* ⚠️ OPCIONAL de verdade — a maioria das tratativas não gera
                    papel de volta. Quando gera (comprovante do cadastro do VT,
                    aditivo assinado, espelho corrigido), é por aqui que ele
                    chega à loja, em vez de voltar por WhatsApp. */}
                <label style={{ ...estilos.rotuloFiltro, marginTop: 16, display: 'block' }}>
                  Anexar resposta ao concluir (opcional)
                </label>
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => setArquivoRh(e.target.files[0] || null)}
                  style={{ ...estilos.input, padding: 8 }}
                />
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                  PDF ou imagem, até 15 MB.
                </div>

                {/* ⚠️ MOTIVO DO CANCELAMENTO — campo próprio de quem trata.
                    Cancelar e concluir são caminhos diferentes: aqui a tratativa
                    é o esperado, mas o RH também pode CANCELAR (ex.: a loja anexou
                    o documento errado), e o backend exige o motivo.
                    ⚠️ Até 21/09 este campo NÃO existia para quem trata — o botão
                    "Cancelar" aparecia, mas sem lugar para digitar o motivo, então
                    voltava sempre com "Informe o motivo" e o cancelamento nunca
                    saía. Era este o "botão que não funciona". */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                  <label style={estilos.rotuloFiltro}>Motivo do cancelamento (só se for cancelar)</label>
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    rows={2}
                    placeholder="Ex.: a loja anexou o documento errado."
                    style={{ ...estilos.input, resize: 'vertical' }}
                  />
                </div>
              </>
            ) : (
              <>
                <label style={estilos.rotuloFiltro}>Motivo do cancelamento</label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={3}
                  placeholder="Ex.: anexei o documento errado."
                  style={{ ...estilos.input, resize: 'vertical' }}
                />
              </>
            ))}

            {feedbackModal && (
              <div style={{
                marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13,
              }}>
                <AlertCircle size={16} /> {feedbackModal.msg}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
              {alvo.status === 'PENDENTE' && podeTratar && defDe(alvo.tipo) && defDe(alvo.tipo).fluxo === 'DECISAO' && (
                <>
                  <button onClick={() => tratar('APROVADO')} disabled={salvando} style={botao('#047857')}>
                    <ThumbsUp size={16} /> Aprovar
                  </button>
                  <button onClick={() => tratar('REPROVADO')} disabled={salvando} style={botao('#b91c1c')}>
                    <ThumbsDown size={16} /> Reprovar
                  </button>
                </>
              )}
              {alvo.status === 'PENDENTE' && podeTratar && defDe(alvo.tipo) && defDe(alvo.tipo).fluxo === 'TRATATIVA' && (
                <button onClick={() => tratar('CONCLUIDO')} disabled={salvando} style={botao('#047857')}>
                  <CheckCircle2 size={16} /> Concluir
                </button>
              )}
              {/* ⚠️ Cancelar só vale para PENDENTE — o backend recusa o resto, e
                  oferecer o botão numa linha encerrada seria prometer o que não
                  se cumpre. */}
              {alvo.status === 'PENDENTE' && (
                <button onClick={cancelar} disabled={salvando} style={botao('#64748b')}>
                  <Ban size={16} /> Cancelar solicitação
                </button>
              )}
              <button
                onClick={() => setAlvo(null)}
                disabled={salvando}
                style={{ ...botao('#fff'), color: '#64748b', border: '1px solid #e2e8f0' }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Cabeçalho que ordena. A seta mostra o estado REAL da coluna: seta dupla
// apagada = "dá para ordenar por aqui", seta cheia = "é por aqui que está
// ordenado". Sem a seta apagada, ninguém descobre que o cabeçalho é clicável.
// ---------------------------------------------------------------------
const ThOrdenavel = ({ campo, ordenacao, aoClicar, children }) => {
  const ativo = ordenacao.campo === campo;
  const Icone = !ativo ? ChevronsUpDown : ordenacao.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none', color: ativo ? COR : estilos.th.color }}
      onClick={() => aoClicar(campo)}
      title="Ordenar por esta coluna"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        <Icone size={13} style={{ opacity: ativo ? 1 : 0.35 }} />
      </span>
    </th>
  );
};

// Botão de anexo da tabela (documento da loja e resposta do RH). A cor separa
// os dois sem precisar de legenda: vermelho do módulo = o que a loja mandou;
// verde = o que o RH devolveu.
const estiloBotaoAnexo = (cor) => ({
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  borderRadius: 6, border: `1px solid ${cor}`, background: '#fff',
  color: cor, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
});

const botao = (fundo) => ({
  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 8,
  border: 'none', background: fundo, color: fundo === '#fff' ? '#64748b' : '#fff',
  fontWeight: 700, fontSize: 14, cursor: 'pointer',
});

export default RHSolicitacoesFila;
